import type { LucideIcon } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { JSX } from 'react';

import { Badge } from '@/components/ui/badge';
import { Image3D } from '@/features/landing/components/image-3d';
import { cn } from '@/lib/utils';

export interface TimelineItemData {
  id: number;
  key: string;
  icon: LucideIcon;
  color: string;
  bgColor: string;
  dotColor?: string;
  customImage: string;
}

interface TimelineItemProps {
  item: TimelineItemData;
  index: number;
  translationNamespace: 'landing.problem' | 'landing.solution';
  variant: 'problem' | 'solution';
}

export function TimelineItem({
  item,
  index,
  translationNamespace,
  variant,
}: TimelineItemProps): JSX.Element {
  const t = useTranslations(translationNamespace);
  const contentOnLeft = index % 2 === 0;
  const dotColor = item.dotColor || item.color.replace('text-', 'bg-');

  return (
    <div className="flex justify-center pt-12 md:pt-40 relative">
      {/* Unified Responsive Layout */}
      <div className="relative grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 w-full items-center md:items-start pl-10 md:pl-0 md:ml-0">
        {/* Desktop Center Dot - positioned at badge level */}
        <div className="absolute left-1/2 top-16 -translate-x-1/2 z-10 hidden md:flex items-center justify-center">
          <div className="h-10 w-10 rounded-full bg-background flex items-center justify-center border border-border shadow-sm">
            <div className={cn('h-4 w-4 rounded-full', dotColor)} />
          </div>
        </div>

        {/* Image Container */}
        <div
          className={cn(
            'relative flex w-full',
            // Mobile: Always first
            'order-1',
            // Desktop: Alternates position, add top margin to align better
            contentOnLeft
              ? 'md:order-1 md:justify-end md:pr-8 md:mt-16'
              : 'md:order-2 md:justify-start md:pl-8 md:mt-16',
          )}
        >
          <Image3D
            lightSrc={item.customImage}
            darkSrc={item.customImage}
            alt={t(`timeline.${item.key}.title`)}
            direction={contentOnLeft ? 'left' : 'right'}
            variant={variant}
          />
        </div>

        {/* Content Container */}
        <div
          className={cn(
            'relative flex w-full',
            // Mobile: Always second
            'order-2',
            // Desktop: Alternates position, same top margin as image
            contentOnLeft
              ? 'md:order-2 md:justify-start md:pl-8 md:mt-16'
              : 'md:order-1 md:justify-end md:pr-8 md:mt-16',
          )}
        >
          {/* Mobile Dot - centered on timeline line at badge level */}
          <div className="absolute -left-10 top-0 -translate-x-1/2 z-10 flex md:hidden items-center justify-center">
            <div className="h-6 w-6 rounded-full bg-background flex items-center justify-center border border-border shadow-sm">
              <div className={cn('h-2 w-2 rounded-full', dotColor)} />
            </div>
          </div>

          <ContentCard
            item={item}
            align="left"
            alignDesktop={contentOnLeft ? 'left' : 'right'}
            translationNamespace={translationNamespace}
          />
        </div>
      </div>
    </div>
  );
}

interface ContentCardProps {
  item: TimelineItemData;
  align: 'left' | 'right';
  alignDesktop?: 'left' | 'right';
  translationNamespace: 'landing.problem' | 'landing.solution';
}

function ContentCard({
  item,
  align,
  alignDesktop,
  translationNamespace,
}: ContentCardProps): JSX.Element {
  const t = useTranslations(translationNamespace);
  const desktopAlign = alignDesktop || align;

  return (
    <div
      className={cn(
        'space-y-4 flex flex-col',
        // Mobile alignment
        align === 'left' ? 'items-start text-left' : 'items-end text-right',
        // Desktop alignment
        desktopAlign === 'right' ? 'md:items-end md:text-right' : 'md:items-start md:text-left',
      )}
    >
      <Badge
        variant="outline"
        className={cn('px-4 py-1.5 text-sm border-transparent', item.bgColor, item.color)}
      >
        <item.icon className="w-4 h-4 mr-2" />
        {t(`timeline.${item.key}.time`)}
      </Badge>

      <h3 className="text-2xl md:text-3xl font-bold">{t(`timeline.${item.key}.title`)}</h3>
      <p className="text-muted-foreground text-lg leading-relaxed">
        {t(`timeline.${item.key}.description`)}
      </p>
      <p className="text-sm italic text-muted-foreground/70 leading-relaxed">
        {t(`timeline.${item.key}.impact`)}
      </p>
    </div>
  );
}
